const PUSH_SERVICE_WORKER_URL = '/sw.js';
const SERVICE_WORKER_READY_TIMEOUT_MS = 10000;

export class PushServiceWorkerError extends Error {
  constructor(message: string, public readonly code: 'unsupported' | 'preview' | 'timeout' | 'registration_failed') {
    super(message);
    this.name = 'PushServiceWorkerError';
  }
}

export const isLovablePreviewContext = () => {
  const hostname = window.location.hostname;
  const isPreviewHost =
    hostname.startsWith('id-preview--') ||
    hostname.startsWith('preview--') ||
    hostname === 'lovableproject.com' ||
    hostname.endsWith('.lovableproject.com') ||
    hostname === 'lovableproject-dev.com' ||
    hostname.endsWith('.lovableproject-dev.com') ||
    hostname === 'beta.lovable.dev' ||
    hostname.endsWith('.beta.lovable.dev') ||
    window.location.search.includes('sw=off');

  let isInIframe = false;
  try {
    isInIframe = window.self !== window.top;
  } catch {
    isInIframe = true;
  }

  return isPreviewHost || isInIframe;
};

export const unregisterPreviewServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) return;

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
};

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, message: string) =>
  Promise.race<T>([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new PushServiceWorkerError(message, 'timeout')), timeoutMs);
    }),
  ]);

export const registerPushServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    throw new PushServiceWorkerError('Service workers are not supported in this browser.', 'unsupported');
  }

  if (!import.meta.env.PROD) {
    await unregisterPreviewServiceWorkers();
    throw new PushServiceWorkerError('Push notifications are available on the published app, not the Lovable preview.', 'preview');
  }

  if (isLovablePreviewContext()) {
    await unregisterPreviewServiceWorkers();
    throw new PushServiceWorkerError('Push notifications are available on the published app, not the Lovable preview.', 'preview');
  }

  try {
    const existingRegistration = await navigator.serviceWorker.getRegistration('/');
    if (!existingRegistration) {
      await navigator.serviceWorker.register(PUSH_SERVICE_WORKER_URL, { scope: '/' });
    }

    return await withTimeout(
      navigator.serviceWorker.ready,
      SERVICE_WORKER_READY_TIMEOUT_MS,
      'Notification service worker did not become ready in time.',
    );
  } catch (error) {
    if (error instanceof PushServiceWorkerError) throw error;
    throw new PushServiceWorkerError('Could not register the notification service worker.', 'registration_failed');
  }
};